import {
  JSX,
  createSignal,
  createSelector,
  Accessor,
  onCleanup,
  splitProps,
  Show,
  createMemo,
  createComputed,
  ErrorBoundary,
  Suspense,
  For,
} from 'solid-js';
import { Ref, resolveFirst } from '@solid-primitives/refs';
import { Portal } from 'solid-js/web';
import { createSwitchTransition } from '@solid-primitives/transition-group';
import { createEventListener } from '@solid-primitives/event-listener';
import gsap from 'gsap';
import cx from 'classnames';
import { setActiveElement } from './ActiveScroll';
import Notice from './Notice';
import Loading from './Loading';
import Icon from './Icon';
import { Toggle } from './Toggle';
import Button from './Button';

const HEAD_SHAKE = 'head-shake';

const [list, setList] = createSignal<PopItem[]>([]);

export const pop: PopCtx = {
  get list() {
    return list();
  },
  push(jsx, opt = {}) {
    const { close = true, onClose } = opt;
    return new Promise<void>((resolve) => {
      setList((pre) => [
        ...pre,
        {
          jsx,
          close,
          onClose: () => {
            resolve();
            onClose && onClose();
          },
        },
      ]);
    });
  },
  confirm(jsx, opt) {
    return new Promise<boolean>((resolve) => {
      const children = () => (
        <Confirm
          {...opt?.()}
          children={jsx as any}
          onConfirm={(v) => {
            resolve(v);
            pop.pop(children);
          }}
        />
      );
      pop.push(children, { close: false });
    });
  },
  pop(option) {
    if (option === true) {
      setList([]);
      return;
    }

    setList((pre) => {
      const clone = [...pre];
      const item = option || clone[clone.length - 1].jsx;
      return clone.filter((it) => {
        const isTarget = it.jsx !== item;
        if (!isTarget) {
          return it.onClose();
        }
        return isTarget;
      });
    });
  },
};

export function PopProvider() {
  const lastPop = () => {
    const arr = pop.list;
    return arr[arr.length - 1];
  };
  const isActive = createSelector(lastPop);
  let overlayer!: HTMLDivElement;

  // 先计算visible，再计算list
  const visible = createMemo(() => pop.list.length > 0);

  const childs = resolveFirst(() => (
    <For each={pop.list}>
      {(it, idx) => {
        return (
          <Toggle when={isActive(it)}>
            <ErrorBoundary
              fallback={(err) => {
                console.log(err);
                return (
                  <Pop>
                    <Notice class="mx-4 mt-10" type="error" children={String(err)} />
                  </Pop>
                );
              }}
            >
              {it.jsx()}
            </ErrorBoundary>
          </Toggle>
        );
      }}
    </For>
  ));

  const duration = 0.3;
  const ease = 'power2.out';
  const cfg = {
    center: [
      { scale: 0.8, opacity: 0 },
      { scale: 1, opacity: 1 },
    ],
    bottomsheet: [{ y: '110%' }, { y: '' }],
  };
  function getTransitionProps(el: HTMLDivElement) {
    if (el.classList.contains('pop-center')) {
      return cfg.center;
    } else {
      return cfg.bottomsheet;
    }
  }
  const trs = createSwitchTransition(childs as () => HTMLDivElement, {
    onEnter(el) {
      el.style.zIndex = '10';
      const [from, to] = getTransitionProps(el);
      gsap.fromTo(el, from, {
        ...to,
        duration,
        ease,
        onComplete() {
          el.style.zIndex = '';
        },
      });
    },
    onExit(el, done) {
      const [from] = getTransitionProps(el);
      gsap.to(el, { ...from, duration, ease, onComplete: done });
    },
  });

  // createRenderEffect(() => console.log(childs(), childs));

  function shake() {
    if (!lastEl) return;

    if (lastEl.className.indexOf(HEAD_SHAKE) === -1) {
      lastEl.classList.toggle(HEAD_SHAKE);
      function aneEnd() {
        lastEl.classList.toggle(HEAD_SHAKE);
        lastEl.removeEventListener('animationend', aneEnd);
      }
      lastEl.addEventListener('animationend', aneEnd);
    }
  }

  createComputed(() => {
    gsap.killTweensOf(overlayer);
    if (visible()) {
      setActiveElement(overlayer, true);
      document.body.appendChild(overlayer);
      gsap.to(overlayer, { '--tw-bg-opacity': 0.8, duration });
    } else {
      gsap.to(overlayer, {
        '--tw-bg-opacity': 0,
        duration,
        onComplete() {
          document.body.removeChild(overlayer);
        },
      });
    }
  });

  let lastEl!: HTMLDivElement;
  return (
    <Portal
      ref={(rel) => {
        overlayer = rel;
        overlayer.className = 'overlayer no-scrollbar';
        createEventListener(overlayer, 'click', (e: any) => {
          if (e.target !== overlayer.firstChild) return;
          const last = lastPop();
          if (!last) return;
          if (last.close) {
            pop.pop();
          } else {
            shake();
          }
        });
      }}
    >
      <Ref
        ref={(el) => {
          if (!el) return;
          lastEl = el as any;
        }}
      >
        <div style={{ height: 'calc(100% + 1px)' }}>{trs as any}</div>
      </Ref>
    </Portal>
  );
}

interface PopProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'title'> {
  type?: 'bottomsheet' | 'center';
  close?: boolean;
  title?: JSX.Element;
  image?: string;
}
export function Pop(props: PopProps) {
  const [, others] = splitProps(props, ['type', 'title', 'children', 'class', 'close', 'image']);
  return (
    <div
      {...others}
      class={cx('relative pt-14 text-center', props.type === 'center' ? 'pop-center' : 'pop-bottomsheet', props.class)}
      style={{
        'box-shadow': '0px -10px 100px 0px #24EE8980',
        'background-color': '#171717',
        'background-image': 'linear-gradient(168.03deg, rgba(44, 238, 136, 0.2) -1.34%, rgba(45, 50, 51, 0) 31.44%)',
      }}
    >
      <Show when={props.close !== false}>
        <Button onClick={() => pop.pop()} class="pop-close z-50">
          <Icon name="Close" />
        </Button>
      </Show>

      <Suspense
        fallback={
          <div class="center flex-1">
            <Loading />
          </div>
        }
      >
        <div class="flex-1 overflow-auto overscroll-contain">
          {Boolean(props.image) && (
            <div
              class="mx-auto mb-5 size-36 rounded-3xl p-5"
              style={{
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
              }}
            >
              <img class="w-full" src={props.image} />
            </div>
          )}
          {Boolean(props.title) && <div class="mb-5 text-3xl font-extrabold">{props.title}</div>}
          {props.children}
        </div>
      </Suspense>
    </div>
  );
}

function Confirm(props: {
  children?: JSX.Element;
  title?: JSX.Element;
  cancel?: JSX.Element;
  confirm?: JSX.Element;
  onConfirm(value: boolean): void;
}) {
  onCleanup(() => {
    props.onConfirm(false);
  });
  return (
    <Pop title={props.title || 'Confirm'} close={false}>
      <div class="text-base text-secondary">{props.children}</div>
      <div class="mb-4 mt-8 flex">
        <Button class="mr-3 flex-1" type="second" onClick={() => props.onConfirm(false)}>
          {props.cancel || 'Cancel'}
        </Button>
        <Button class="flex-1" onClick={() => props.onConfirm(true)} type="brand">
          {props.confirm || 'Confirm'}
        </Button>
      </div>
    </Pop>
  );
}

interface PopItem {
  jsx: Accessor<JSX.Element>;
  /** 是否允许关闭, 如果不允许则不能点击overlayer进行关闭，需要手动调用api进行关闭 */
  close: boolean;
  onClose(value?: any): void;
}

type PopItemOptions = Partial<Omit<PopItem, 'jsx'>>;

interface PopCtx {
  get list(): PopItem[];
  push(jsx: Accessor<JSX.Element>, option?: PopItemOptions): Promise<any>;
  confirm(
    jsx: Accessor<JSX.Element>,
    option?: Accessor<{
      cancel?: JSX.Element;
      confirm?: JSX.Element;
      title?: JSX.Element;
    }>
  ): Promise<boolean>;
  pop(com?: Accessor<JSX.Element> | true): void;
}
